/**
 * Channel Header - Shows channel info at the top of message area
 */

import React, { useState, useEffect } from 'react';
import { Typography, Button, Dropdown, Menu, Avatar, Space, Tag, Tooltip, Modal, message, Input, List } from 'antd';
import { 
  InfoCircleOutlined, 
  StarOutlined, 
  StarFilled,
  PushpinOutlined,
  UserOutlined,
  SettingOutlined,
  MoreOutlined,
  SearchOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';
import { useChatWebSocket } from '../../contexts/ChatWebSocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { PresenceIndicator } from './PresenceIndicator';
import './ChannelHeader.css';

const { Text, Title } = Typography;

interface ChannelHeaderProps {
  channelId: string;
  onToggleInfo?: () => void;
}

type PresenceStatus = 'online' | 'away' | 'busy' | 'offline';

const normalizeStatus = (status: string | undefined): PresenceStatus => {
  if (status === 'online' || status === 'away' || status === 'busy' || status === 'offline') {
    return status;
  }
  return 'offline';
};

export const ChannelHeader: React.FC<ChannelHeaderProps> = ({ channelId, onToggleInfo }) => {
  const { presence } = useChatWebSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showMembers, setShowMembers] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [pinnedVisible, setPinnedVisible] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [renameVisible, setRenameVisible] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Fetch channel details
  const { data: channelData } = useQuery(
    ['channel-details', channelId],
    () => api.get(`/api/chat/channels/${channelId}`).then(res => res.data),
    {
      enabled: !!channelId,
    }
  );

  // Fetch channel members
  const { data: membersData } = useQuery(
    ['channel-members', channelId],
    () => api.get(`/api/chat/channels/${channelId}/members`).then(res => res.data),
    {
      enabled: !!channelId,
    }
  );

  // Fetch pinned messages
  const { data: pinnedData } = useQuery(
    ['pinned-messages', channelId],
    () => api.get(`/api/chat/channels/${channelId}/pinned`).then(res => res.data),
    {
      enabled: !!channelId && pinnedVisible,
    }
  );

  const channel = channelData?.channel;
  const members = membersData?.members || [];

  // Check if channel is favorited
  useEffect(() => {
    if (channel) {
      api.get('/api/chat/channels').then(res => {
        const favChannel = res.data.channels.find((c: any) => c.id === channelId);
        setIsFavorited(favChannel?.is_starred || favChannel?.isStarred || false);
      }).catch(() => {
        // Ignore errors
      });
    }
  }, [channel, channelId]);

  // Get display name for channel
  const getChannelDisplayName = () => {
    if (!channel) return 'Channel';
    
    // For direct messages, show the other user's name
    if (channel.type === 'direct') {
      // Try to get from members
      const otherMember = members.find((m: any) => m.id !== channel.created_by);
      if (otherMember) {
        const firstName = otherMember.first_name || otherMember.firstName || '';
        const lastName = otherMember.last_name || otherMember.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || otherMember.email || 'Direct Message';
      }
      // Fallback to other_user if available
      if (channel.other_user) {
        const firstName = channel.other_user.first_name || '';
        const lastName = channel.other_user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || channel.other_user.email || 'Direct Message';
      }
      if (channel.otherUser) {
        const firstName = channel.otherUser.firstName || '';
        const lastName = channel.otherUser.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || channel.otherUser.email || 'Direct Message';
      }
      return 'Direct Message';
    }
    
    // For group channels, show the name
    return channel.name || 'Unnamed Channel';
  };

  const getMemberStatus = (member: any) => {
    const userPresence = presence[member.id] || null;
    return userPresence?.status 
      ? normalizeStatus(userPresence.status)
      : normalizeStatus(member.presence_status || member.presenceStatus);
  };

  const handleDeleteChannel = async () => {
    try {
      await api.delete(`/api/chat/channels/${channelId}`);
      message.success('Channel deleted successfully');
      queryClient.invalidateQueries('chat-channels');
      setDeleteConfirmVisible(false);
      // Navigate away or clear selection
      window.location.reload(); // Simple reload for now
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete channel');
    }
  };

  const handleToggleFavorite = async () => {
    try {
      const response = await api.put(`/api/chat/channels/${channelId}/favorite`);
      setIsFavorited(response.data.is_starred || response.data.isStarred);
      queryClient.invalidateQueries('chat-channels');
      message.success(response.data.is_starred || response.data.isStarred ? 'Added to favorites' : 'Removed from favorites');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to update favorite');
    }
  };

  const handleUnpinMessage = async (messageId: string) => {
    try {
      await api.post(`/api/chat/channels/${channelId}/messages/${messageId}/unpin`);
      queryClient.invalidateQueries(['pinned-messages', channelId]);
      message.success('Message unpinned');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to unpin message');
    }
  };

  const handleRenameChannel = async () => {
    if (!renameValue.trim()) {
      message.error('Channel name cannot be empty');
      return;
    }

    if (renameValue.trim() === channel?.name) {
      setRenameVisible(false);
      return;
    }

    setRenaming(true);
    try {
      await api.put(`/api/chat/channels/${channelId}`, { name: renameValue.trim() });
      queryClient.invalidateQueries(['channel-details', channelId]);
      queryClient.invalidateQueries('chat-channels');
      message.success('Channel renamed successfully');
      setRenameVisible(false);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to rename channel');
    } finally {
      setRenaming(false);
    }
  };

  // Initialize rename value when modal opens
  useEffect(() => {
    if (renameVisible && channel) {
      setRenameValue(channel.name || '');
    }
  }, [renameVisible, channel]);

  const menuItems = [
    {
      key: 'info',
      icon: <InfoCircleOutlined />,
      label: 'Channel Details',
      onClick: onToggleInfo,
    },
    {
      key: 'members',
      icon: <UserOutlined />,
      label: `Members (${members.length})`,
      onClick: () => setShowMembers(!showMembers),
    },
    {
      key: 'pinned',
      icon: <PushpinOutlined />,
      label: 'Pinned Messages',
      onClick: () => setPinnedVisible(!pinnedVisible),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Channel Settings',
      onClick: () => {
        // TODO: Implement channel settings modal
        message.info('Channel settings coming soon');
      },
    },
    ...(isAdmin && channel?.type !== 'direct' ? [
      {
        key: 'rename',
        icon: <EditOutlined />,
        label: 'Rename Channel',
        onClick: () => setRenameVisible(true),
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete Channel',
        danger: true,
        onClick: () => setDeleteConfirmVisible(true),
      }
    ] : []),
  ].map(item => ({
    ...item,
    onClick: () => {
      if (item.onClick) item.onClick();
    },
  }));

  return (
    <div className="channel-header">
      <div className="channel-header-left">
        <div className="channel-header-title">
          <Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {getChannelDisplayName()}
          </Title>
          {channel?.description && (
            <Text type="secondary" style={{ fontSize: 13, marginLeft: 8 }}>
              {channel.description}
            </Text>
          )}
        </div>
        {channel && (
          <div className="channel-header-meta">
            <Tag color={channel.is_private || channel.isPrivate ? 'red' : 'blue'} style={{ marginRight: 8 }}>
              {channel.is_private || channel.isPrivate ? 'Private' : 'Public'}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </Text>
          </div>
        )}
      </div>

      <div className="channel-header-right">
        <Space size="middle">
          <Tooltip title="Search Messages">
            <Button 
              type="text" 
              icon={<SearchOutlined />} 
              onClick={() => setSearchVisible(!searchVisible)}
            />
          </Tooltip>
          <Tooltip title={isFavorited ? "Remove from favorites" : "Add to favorites"}>
            <Button 
              type="text" 
              icon={isFavorited ? <StarFilled /> : <StarOutlined />}
              onClick={handleToggleFavorite}
              style={{ color: isFavorited ? '#f5a623' : undefined }}
            />
          </Tooltip>
          <Tooltip title="Pinned Messages">
            <Button 
              type="text" 
              icon={<PushpinOutlined />}
              onClick={() => setPinnedVisible(!pinnedVisible)}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      </div>

      {showMembers && members.length > 0 && (
        <div className="channel-header-members">
          <div className="channel-header-members-list">
            {members.slice(0, 10).map((member: any) => {
              const firstName = member.first_name || member.firstName || '';
              const lastName = member.last_name || member.lastName || '';
              const displayName = `${firstName} ${lastName}`.trim() || member.email || 'Unknown';
              const status = getMemberStatus(member);
              
              return (
                <Tooltip key={member.id} title={displayName}>
                  <PresenceIndicator status={status} size="small">
                    <Avatar 
                      size="small" 
                      src={member.avatar_url} 
                      icon={<UserOutlined />}
                      style={{ cursor: 'pointer' }}
                    />
                  </PresenceIndicator>
                </Tooltip>
              );
            })}
            {members.length > 10 && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                +{members.length - 10} more
              </Text>
            )}
          </div>
        </div>
      )}

      {/* Search Modal */}
      <Modal
        title="Search Messages"
        open={searchVisible}
        onCancel={() => setSearchVisible(false)}
        footer={null}
        width={600}
      >
        <Input
          placeholder="Search messages in this channel..."
          prefix={<SearchOutlined />}
          style={{ marginBottom: 16 }}
        />
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text type="secondary">Search functionality coming soon</Text>
        </div>
      </Modal>

      {/* Pinned Messages Modal */}
      <Modal
        title="Pinned Messages"
        open={pinnedVisible}
        onCancel={() => setPinnedVisible(false)}
        footer={null}
        width={600}
      >
        {pinnedData?.messages && pinnedData.messages.length > 0 ? (
          <List
            dataSource={pinnedData.messages}
            renderItem={(msg: any) => {
              const firstName = msg.user?.first_name || msg.user?.firstName || '';
              const lastName = msg.user?.last_name || msg.user?.lastName || '';
              const userName = `${firstName} ${lastName}`.trim() || msg.user?.email || 'Unknown';
              
              return (
                <List.Item
                  actions={[
                    <Button
                      key="unpin"
                      type="text"
                      size="small"
                      onClick={() => handleUnpinMessage(msg.id)}
                    >
                      Unpin
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={userName}
                    description={msg.text || msg.content}
                  />
                </List.Item>
              );
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text type="secondary">No pinned messages</Text>
          </div>
        )}
      </Modal>

      <Modal
        title="Delete Channel"
        open={deleteConfirmVisible}
        onOk={handleDeleteChannel}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this channel? This action cannot be undone.</p>
      </Modal>

      {/* Rename Channel Modal */}
      <Modal
        title="Rename Channel"
        open={renameVisible}
        onOk={handleRenameChannel}
        onCancel={() => setRenameVisible(false)}
        okText="Rename"
        confirmLoading={renaming}
      >
        <Input
          placeholder="Enter new channel name"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={handleRenameChannel}
          maxLength={255}
          autoFocus
        />
      </Modal>
    </div>
  );
};

